import { CURRENCY_SYMBOL } from '../utils/currency';
import React, { useState, useEffect } from 'react';
import {
  AlertCircle,
  DollarSign,
  Clock,
  XCircle,
  CheckCircle,
  TrendingDown,
  RefreshCw,
  Search,
  AlertTriangle,
  RotateCcw,
  User,
  MapPin,
  TrendingUp,
  Wallet,
  ArrowDownCircle
} from 'lucide-react';
import { vendorApi } from '../services/api';
import { useQuery } from '@tanstack/react-query';
import RefundDetailsModal from '../components/modals/RefundDetailsModal';

const DebtsAndRefunds: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'refunds' | 'debts'>('refunds');
  const [venues, setVenues] = useState<any[]>([]);
  const [selectedVenueId, setSelectedVenueId] = useState<number | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state for refund details
  const [selectedRefund, setSelectedRefund] = useState<any>(null);
  const [showRefundModal, setShowRefundModal] = useState(false);

  // Fetch venues on mount
  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const data = await vendorApi.getMyVenues();
        setVenues(data);
        if (data.length > 0) {
          setSelectedVenueId(data[0].id);
        }
      } catch (err: any) {
        console.error('Failed to fetch venues:', err);
      }
    };
    fetchVenues();
  }, []);

  // Fetch refunds with React Query
  const { data: refundsData, isLoading: refundsLoading, refetch: refetchRefunds } = useQuery({
    queryKey: ['refunds', selectedVenueId, statusFilter, dateFrom, dateTo],
    queryFn: async () => {
      if (!selectedVenueId) return null;
      const filters: {
        status?: string;
        date_from?: string;
        date_to?: string;
        venue_id?: number;
      } = {
        venue_id: selectedVenueId,
      };
      if (statusFilter && statusFilter !== 'all') filters.status = statusFilter;
      if (dateFrom) filters.date_from = dateFrom;
      if (dateTo) filters.date_to = dateTo;

      const response = await vendorApi.getRefunds(filters);
      return response;
    },
    enabled: !!selectedVenueId && activeTab === 'refunds',
    staleTime: 2 * 60 * 1000,
    refetchInterval: activeTab === 'refunds' ? 2 * 60 * 1000 : false,
  });

  // Fetch debts with React Query
  const { data: debtsData, isLoading: debtsLoading, refetch: refetchDebts } = useQuery({
    queryKey: ['debts', selectedVenueId],
    queryFn: async () => {
      if (!selectedVenueId) return null;
      return await vendorApi.getDebts({ venue_id: selectedVenueId });
    },
    enabled: !!selectedVenueId && activeTab === 'debts',
    staleTime: 2 * 60 * 1000,
  });

  const refunds = refundsData?.data || [];
  const refundsMeta = refundsData?.meta || null;
  const debts = debtsData?.data || [];
  const debtSummary = debtsData?.summary || null;

  // Calculate KPI stats
  const stats = {
    totalRefunds: refundsMeta?.total_refunds || 0,
    totalRefundAmount: refundsMeta?.total_amount || 0,
    pendingRefunds: refunds.filter((r: any) => r.status === 'processing').length,
    processedRefunds: refunds.filter((r: any) => r.status === 'processed').length,
    totalDebts: debtSummary?.total_debt || 0,
    pendingDebts: debtSummary?.pending_debt || 0,
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return `${CURRENCY_SYMBOL}${amount.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge for refunds
  const getRefundStatusBadge = (status: string, paystackStatus?: string) => {
    if (status === 'processing') {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-blue-100 text-blue-700">
          <Clock className="w-3 h-3 mr-1" />
          Processing
        </span>
      );
    }
    if (status === 'processed') {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3 mr-1" />
          Processed
        </span>
      );
    }
    if (status === 'failed') {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-red-100 text-red-700">
          <XCircle className="w-3 h-3 mr-1" />
          Failed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-gray-100 text-gray-700">
        {status}
      </span>
    );
  };

  // Get status badge for debts
  const getDebtStatusBadge = (status: string) => {
    if (status === 'pending') {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-orange-100 text-orange-700">
          <AlertCircle className="w-3 h-3 mr-1" />
          Pending
        </span>
      );
    }
    if (status === 'cleared') {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3 mr-1" />
          Cleared
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-gray-100 text-gray-700">
        {status}
      </span>
    );
  };

  // Filter refunds by search term
  const filteredRefunds = refunds.filter((refund: any) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      refund.request_reference?.toLowerCase().includes(search) ||
      refund.booking_reference?.toLowerCase().includes(search) ||
      refund.customer_name?.toLowerCase().includes(search)
    );
  });

  // Filter debts by search term
  const filteredDebts = debts.filter((debt: any) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      debt.debt_reference?.toLowerCase().includes(search) ||
      debt.refund_reference?.toLowerCase().includes(search) ||
      debt.booking_reference?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* Header with Venue Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900 mb-1">Debts & Refunds</h1>
          <p className="text-gray-600 text-sm">
            Manage refund requests and track debt balances
          </p>
        </div>
        {venues.length > 1 && (
          <select
            value={selectedVenueId || ''}
            onChange={(e) => setSelectedVenueId(Number(e.target.value))}
            className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            {venues.map((venue) => (
              <option key={venue.id} value={venue.id}>
                {venue.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* KPI Cards - Matching BookingsNew Design */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Total Refunds */}
        <div className="bg-white rounded-xl p-3 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-gray-600 text-xs mb-1 truncate">Total Refunds</p>
              {refundsLoading ? (
                <div className="h-7 w-12 bg-gray-200 animate-pulse rounded" />
              ) : (
                <p className="text-lg sm:text-xl font-black text-gray-900 truncate">{stats.totalRefunds}</p>
              )}
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0 ml-2">
              <ArrowDownCircle className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>

        {/* Pending Refunds */}
        <div className="bg-white rounded-xl p-3 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-gray-600 text-xs mb-1 truncate">Pending Refunds</p>
              {refundsLoading ? (
                <div className="h-7 w-12 bg-gray-200 animate-pulse rounded" />
              ) : (
                <p className="text-lg sm:text-xl font-black text-gray-900 truncate">{stats.pendingRefunds}</p>
              )}
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0 ml-2">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>

        {/* Total Debts */}
        <div className="bg-white rounded-xl p-3 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-gray-600 text-xs mb-1 truncate">Total Debts</p>
              {debtsLoading ? (
                <div className="h-7 w-20 bg-gray-200 animate-pulse rounded" />
              ) : (
                <p className="text-lg sm:text-xl font-black text-gray-900 truncate">{formatCurrency(stats.totalDebts)}</p>
              )}
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center flex-shrink-0 ml-2">
              <Wallet className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>

        {/* Pending Debts */}
        <div className="bg-white rounded-xl p-3 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-gray-600 text-xs mb-1 truncate">Pending Debts</p>
              {debtsLoading ? (
                <div className="h-7 w-20 bg-gray-200 animate-pulse rounded" />
              ) : (
                <p className="text-lg sm:text-xl font-black text-gray-900 truncate">{formatCurrency(stats.pendingDebts)}</p>
              )}
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-yellow-50 text-yellow-600 flex items-center justify-center flex-shrink-0 ml-2">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs & Search - Matching BookingsNew Design */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-200">
          {/* Tab Buttons */}
          <div className="flex items-center overflow-x-auto px-3 sm:px-4">
            <button
              onClick={() => setActiveTab('refunds')}
              className={`px-3 sm:px-5 py-2 sm:py-3 font-semibold text-xs sm:text-sm transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'refunds'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Refunds <span className="text-gray-400">({stats.totalRefunds})</span>
            </button>
            <button
              onClick={() => setActiveTab('debts')}
              className={`px-3 sm:px-5 py-2 sm:py-3 font-semibold text-xs sm:text-sm transition-all border-b-2 whitespace-nowrap ${
                activeTab === 'debts'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Debts <span className="text-gray-400">({debts.length})</span>
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative px-3 py-2 sm:px-4 sm:py-2">
            <Search className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 absolute left-6 sm:left-7 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500 w-full sm:w-48 md:w-64 text-xs sm:text-sm"
            />
          </div>
        </div>
      </div>

      {/* Content Area */}
      {activeTab === 'refunds' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Filters for Refunds */}
          <div className="p-3 sm:p-4 border-b border-gray-200">
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg font-semibold text-xs sm:text-sm bg-white text-gray-700 outline-none focus:border-blue-500 cursor-pointer"
              >
                <option value="all">All Status</option>
                <option value="processing">Processing</option>
                <option value="processed">Processed</option>
                <option value="failed">Failed</option>
              </select>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="From Date"
                className="px-3 py-2 border border-gray-200 rounded-lg font-semibold text-xs sm:text-sm bg-white text-gray-700 outline-none focus:border-blue-500"
              />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="To Date"
                className="px-3 py-2 border border-gray-200 rounded-lg font-semibold text-xs sm:text-sm bg-white text-gray-700 outline-none focus:border-blue-500"
              />
            </div>
          </div>

          {/* Refunds Table */}
          {refundsLoading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Loading refunds...</p>
            </div>
          ) : filteredRefunds.length === 0 ? (
            <div className="p-12 text-center">
              <TrendingDown className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No refunds found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Refund Reference
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Booking
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Available Balance
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRefunds.map((refund: any, index: number) => (
                    <tr
                      key={refund.id}
                      className="border-b border-gray-100 hover:bg-blue-50 cursor-pointer transition-colors"
                      onClick={() => {
                        setSelectedRefund(refund);
                        setShowRefundModal(true);
                      }}
                    >
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-gray-900">{refund.request_reference}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-gray-900">{refund.booking_reference}</p>
                        <p className="text-xs text-gray-500">{refund.venue_name}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-2">
                            <User className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{refund.customer_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="text-base font-semibold text-red-600">-{formatCurrency(refund.vendor_share || refund.requested_amount)}</p>
                        {refund.vendor_share && refund.vendor_share !== refund.requested_amount && (
                          <p className="text-xs text-gray-500">Customer: {formatCurrency(refund.requested_amount)}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="text-sm font-bold text-gray-900">{formatCurrency(refund.vendor_balance_at_refund || 0)}</p>
                        <p className="text-xs text-gray-500">for payouts</p>
                      </td>
                      <td className="px-4 py-3">
                        {getRefundStatusBadge(refund.status, refund.paystack_status)}
                        {refund.covered_by_balance && (
                          <p className="text-xs text-green-600 mt-1">✓ Platform covered</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900">{formatDate(refund.requested_at)}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'debts' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Debts Table */}
          {debtsLoading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Loading debts...</p>
            </div>
          ) : filteredDebts.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
              <p className="text-gray-500">No debts - you're all clear!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Debt Reference
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Refund/Booking
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Reason
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDebts.map((debt: any) => (
                    <tr key={debt.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-gray-900">{debt.debt_reference}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-gray-900">{debt.refund_reference || 'N/A'}</p>
                        <p className="text-xs text-gray-500">{debt.booking_reference}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900">{debt.reason || 'Refund covered by platform'}</p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="text-base font-semibold text-orange-600">{formatCurrency(debt.amount)}</p>
                      </td>
                      <td className="px-4 py-3">
                        {getDebtStatusBadge(debt.status)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900">{formatDate(debt.created_at)}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Refund Details Modal */}
      <RefundDetailsModal
        isOpen={showRefundModal}
        onClose={() => setShowRefundModal(false)}
        refund={selectedRefund}
        onRetrySuccess={() => refetchRefunds()}
      />
    </div>
  );
};

export default DebtsAndRefunds;
